import React, { useCallback, useEffect, useState } from 'react';
import { Input, Select, Form, message, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../../contexts/AuthContext';

const { Option } = Select;

const useFields = () => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const [enums, setEnums] = useState({});
  const [positionEnums, setPositionEnums] = useState([]);

  const getFields = (otherPositionSelected, setOtherPositionSelected) => {
    return [
      {
        name: ['gender'],
        input: (
          <Select loading={isFieldsLoading}>
            {enums.genders &&
              enums.genders.map((g) => (
                <Option value={g} key={g}>
                  {t(`insurers.form.${g}`)}
                </Option>
              ))}
          </Select>
        ),
        rules: [{ required: true }]
      },
      {
        name: ['last_name'],
        rules: [{ required: true }]
      },
      {
        name: ['first_name'],
        rules: [{ required: true }]
      },
      {
        name: ['email'],
        rules: [{ required: true }, { type: 'email' }]
      },
      {
        name: ['phone_number'],
        input: (
          <Form.Item noStyle name={['phone_number']}>
            <Input
              minLength={10}
              maxLength={10}
              style={{
                width: '72%',
                borderRadius: 8,
                height: 40
              }}
            />
          </Form.Item>
        )
      },
      {
        name: ['position'],
        input: (
          <Input.Group compact>
            <Form.Item noStyle name={['position', 'value']}>
              <Select
                loading={isFieldsLoading}
                style={{ width: '100%' }}
                onChange={(value) => {
                  setOtherPositionSelected(value);
                }}
              >
                {positionEnums &&
                  positionEnums.map((g) => (
                    <Option value={g} key={g}>
                      {t(`insurers.form.enums.${g}`)}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            {otherPositionSelected === 'OTHER' && (
              <Form.Item noStyle name={['position', 'comment']}>
                <Input
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    marginTop: 10,
                    height: 40
                  }}
                />
              </Form.Item>
            )}
          </Input.Group>
        ),
        rules: [{ required: true }]
      }
    ];
  };

  const getBlocked_fit_fields = (index) => {
    return [
      {
        label: t('settings.team.form.commission_rate'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'blocked_fit',
          'commission_rate'
        ],
        initialValue: 40,
        translate: false,
        rules: [{ required: true }],
        input: (
          <InputNumber
            min={0}
            max={40}
            formatter={(value) => `${value}%`}
            parser={(value) => value.replace('%', '')}
          />
        )
      },

      {
        label: t('settings.team.form.brokerage_fees'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'blocked_fit',
          'brokerage_fees'
        ],
        translate: false,
        initialValue: 20,
        rules: [{ required: true }],
        input: (
          <InputNumber
            min={0}
            max={20}
            formatter={(value) => `${value}€`}
            parser={(value) => value.replace('€', '')}
          />
        )
      },
      {
        label: t('settings.team.form.administration_fees'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'blocked_fit',
          'administration_fees'
        ],
        translate: false,
        initialValue: 500,
        rules: [{ required: true }],
        input: (
          <InputNumber
            min={0}
            max={500}
            formatter={(value) => `${value}€`}
            parser={(value) => value.replace('€', '')}
          />
        )
      }
    ];
  };

  const getBlocked_fit_fields_prev = (index) => {
    return [
      {
        label: t('settings.team.form.commission_rate'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'blocked_fit_prev',
          'commission_rate'
        ],
        initialValue: 40,
        translate: false,
        rules: [{ required: true }],
        input: (
          <InputNumber
            min={0}
            max={40}
            formatter={(value) => `${value}%`}
            parser={(value) => value.replace('%', '')}
          />
        )
      },
      {
        label: t('settings.team.form.brokerage_fees'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'blocked_fit_prev',
          'brokerage_fees'
        ],
        translate: false,
        initialValue: 10,
        rules: [{ required: true }],
        input: (
          <InputNumber
            min={0}
            max={10}
            formatter={(value) => `${value}€`}
            parser={(value) => value.replace('€', '')}
          />
        )
      },
      {
        label: t('settings.team.form.administration_fees'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'blocked_fit_prev',
          'administration_fees'
        ],
        translate: false,
        initialValue: 500,
        rules: [{ required: true }],
        input: (
          <InputNumber
            min={0}
            max={500}
            formatter={(value) => `${value}€`}
            parser={(value) => value.replace('€', '')}
          />
        )
      }
    ];
  };

  const getControlled_fit_fields = (index) => {
    return [
      {
        label: t('settings.team.form.commission_rate'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'controlled_fit',
          'commission_rate'
        ],
        translate: false,
        input: (
          <Input.Group>
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'commission_rate_minVal'
              ]}
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={40}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
              />
            </Form.Item>
            {` à `}
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'commission_rate_maxVal'
              ]}
              initialValue={40}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={40}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
              />
            </Form.Item>
          </Input.Group>
        )
      },
      {
        label: t('settings.team.form.brokerage_fees'),
        translate: false,
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'controlled_fit',
          'brokerage_fees'
        ],
        input: (
          <Input.Group>
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'brokerage_fees_minVal'
              ]}
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={20}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
            {` à `}
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'brokerage_fees_maxVal'
              ]}
              initialValue={20}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={20}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
          </Input.Group>
        )
      },
      {
        label: t('settings.team.form.administration_fees'),
        translate: false,
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'controlled_fit',
          'administration_fees'
        ],
        input: (
          <Input.Group>
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'administration_fees_minVal'
              ]}
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={500}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
            {` à `}
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'administration_fees_maxVal'
              ]}
              initialValue={500}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={500}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
          </Input.Group>
        )
      }
    ];
  };

  const getControlled_fit_fields_prev = (index) => {
    return [
      {
        label: t('settings.team.form.commission_rate'),
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'controlled_fit',
          'commission_rate'
        ],
        translate: false,
        input: (
          <Input.Group>
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'commission_rate_minVal'
              ]}
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={40}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
              />
            </Form.Item>
            {` à `}
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'commission_rate_maxVal'
              ]}
              initialValue={40}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={40}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
              />
            </Form.Item>
          </Input.Group>
        )
      },
      {
        label: t('settings.team.form.brokerage_fees'),
        translate: false,
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'controlled_fit',
          'brokerage_fees'
        ],
        input: (
          <Input.Group>
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit_prev',
                'brokerage_fees_minVal'
              ]}
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={10}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
            {` à `}
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit_prev',
                'brokerage_fees_maxVal'
              ]}
              initialValue={10}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={10}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
          </Input.Group>
        )
      },
      {
        label: t('settings.team.form.administration_fees'),
        translate: false,
        name: [
          'permissionManagement',
          'products_access',
          index,
          'advancedManagement',
          'controlled_fit',
          'administration_fees'
        ],
        input: (
          <Input.Group>
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'administration_fees_minVal'
              ]}
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={500}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
            {` à `}
            <Form.Item
              noStyle
              name={[
                'permissionManagement',
                'products_access',
                index,
                'advancedManagement',
                'controlled_fit',
                'administration_fees_maxVal'
              ]}
              initialValue={500}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={500}
                formatter={(value) => `${value}€`}
                parser={(value) => value.replace('€', '')}
              />
            </Form.Item>
          </Input.Group>
        )
      }
    ];
  };

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/insurers/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message.error(e.response.status);
    }
  };

  const getPositionEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: '/users/positionEnums'
      });
      setPositionEnums(data.positions);
    } catch (e) {
      if (e.response) message.error(e.response.status);
    }
  };

  const getSelectOptions = useCallback(async () => {
    setIsFieldsLoading(true);
    await getEnums();
    await getPositionEnums();
    setIsFieldsLoading(false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      await getSelectOptions();
    })();
  }, [getSelectOptions]);

  return {
    getFields,
    getBlocked_fit_fields,
    getControlled_fit_fields,
    getBlocked_fit_fields_prev,
    getControlled_fit_fields_prev,
    isFieldsLoading
  };
};

export default useFields;
